import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Building2, ChevronRight, Search } from "lucide-react";
import api from "../lib/api";
import Layout from "../components/Layout";

const Companies = () => {
  const [entities, setEntities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/enterprise/entities");
      setEntities(data);
    } catch (err) {
      toast.error("Could not load companies");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = entities.filter((e) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      e.name?.toLowerCase().includes(q) ||
      e.abbreviation?.toLowerCase().includes(q) ||
      e.entity_type?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <section className="mb-6">
        <p className="label-eyebrow">Portfolio</p>
        <h1 className="font-heading text-4xl sm:text-5xl tracking-tight mt-2">Companies</h1>
        <p className="text-textmuted mt-3 max-w-2xl leading-relaxed">
          Every business, subsidiary, division, brand and program under R.S.V.P. Select one to
          drill into its dedicated dashboard.
        </p>
      </section>

      <div className="relative mb-5 max-w-md">
        <Search className="w-4 h-4 text-textmuted absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search companies…"
          data-testid="company-search"
          className="w-full bg-obsidian border border-seam rounded-md pl-10 pr-4 py-2.5 text-sm text-textmain focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 className="w-6 h-6 text-champagne animate-spin" />
        </div>
      ) : (
        <div className="regal-card overflow-hidden">
          <div className="divide-y divide-seam">
            {filtered.map((e) => (
              <button
                key={e.id}
                type="button"
                onClick={() => navigate(`/companies/${e.id}`)}
                data-testid={`co-row-${e.id}`}
                className="w-full text-left px-5 py-4 hover:bg-elevated/40 flex items-center gap-4 group"
              >
                <Building2 className="w-5 h-5 text-champagne" strokeWidth={1.4} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg text-textmain truncate">{e.name}</span>
                    {e.abbreviation && (
                      <span className="text-[10px] tracking-wider uppercase text-champagne">{e.abbreviation}</span>
                    )}
                  </div>
                  <p className="text-xs text-textmuted mt-0.5">
                    {e.entity_type} · {e.status}
                    {e.location ? ` · ${e.location}` : ""}
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-textmuted group-hover:text-champagne" strokeWidth={1.5} />
              </button>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Companies;
